import React, { useState, useEffect, useCallback , useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, addWeeks, subWeeks, subMonths, isBefore } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  BookOpen, Calendar, ChevronLeft, ChevronRight, Clock,
  FileText, Users, Check, X, AlertTriangle, Save,
  ArrowLeft, ClipboardList, Eye, Pencil, UserCheck, LogOut, ShieldCheck, MessageSquare,
  Plus, GraduationCap, User, CheckSquare, CheckCircle2, Award, ClipboardCheck, Loader2, TrendingUp, ArrowRightLeft, Target, CornerDownRight
} from 'lucide-react';
import { ActiviteFormateurDialog, activiteColor } from '../components/formateur/ActiviteFormateurDialog';
import { RappelEmargements } from '../components/formateur/RappelEmargements';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import WorkflowPanel from '../components/WorkflowPanel';
import { NotesPanel } from '../components/NotesPanel';
import { CandidatEditDialog } from '../components/CandidatEditDialog';
import { PresencesExport } from '../components/PresencesExport';
import { AssiduitePanel } from '../components/AssiduitePanel';
import { CandidatsCadre } from './formateur/journee/CandidatsCadre';

import QCMFormateurPanel from '../components/QCMFormateurPanel';
import EvaluationJournee from '../components/EvaluationJournee';
import { DispatchModulesSection } from '../components/DispatchModulesSection';
import { ObservationsCandidatsPanel } from '../components/ObservationsCandidatsPanel';
import { FicheCandidatAction } from '../components/candidat/FicheCandidatAction';
import { CandidatLink } from '../components/candidat/CandidatLink';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { InstallBanner } from '../components/pwa/InstallBanner';
import { getAuthToken } from '../lib/authToken';
import { RapportDossierPanel } from './rapports/RapportDossierPanel';

import { JourneeView } from './formateur/journee/JourneeView';
import { CompetencesProList } from '../components/CompetencesPro';
import { SuiviPedagogique } from '../components/suivi/SuiviPedagogique';
import { FicheCandidatEntreprise } from '../components/entreprise/FicheCandidatEntreprise';
import { TestsLiesModule } from '../components/formation/TestsLiesModule';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PortailFormateurPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dossiers, setDossiers] = useState([]);
  const [selectedJournee, setSelectedJournee] = useState(null); // {dossier_id, date}
  const [journeeData, setJourneeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [modules, setModules] = useState([]);
  const [selectedAction, setSelectedAction] = useState(null); // Action detail view
  const [actionTab, setActionTab] = useState('detail');
  const [rapportsVue, setRapportsVue] = useState('rapport');
  const [actionData, setActionData] = useState(null);
  const [alertesNA, setAlertesNA] = useState([]);
  const [activites, setActivites] = useState([]); // activités hors production (dév. pédagogique, CP...)
  const [activiteOpen, setActiviteOpen] = useState(null);

  // Admin: view as a specific formateur
  const isAdmin = ['admin', 'super_admin'].includes(user?.role);
  const [formateurs, setFormateurs] = useState([]);
  const [viewAsFormateur, setViewAsFormateur] = useState(null);
  const [hasEvaluation, setHasEvaluation] = useState(false);

  // Cahier de texte form state
  const [cahier, setCahier] = useState({
    module_realise_id: '',
    module_realise_titre: '',
    modules_realises: [], // [{module_id, titre, duree_heures}]
    modules_confirmes: false,
    presences: [],
    presences_validees: false,
    abandons: [],
    deroule: '',
    exercices: '',
    cas_pratique: '',
    observations_groupe: '',
    actions_preconisations: '',
    evaluations_realisees: [],
    observations_candidats: [],
    modules_candidats: [],
    dispatch_modules: [],
  });

  const fetchDossiers = useCallback(async () => {
    try {
      const params = viewAsFormateur ? `?view_as=${viewAsFormateur.id}` : '';
      const [resp, actResp] = await Promise.all([
        axios.get(`${API_URL}/portail/formateur/dossiers${params}`),
        axios.get(`${API_URL}/portail/formateur/activites${params}`).catch(() => ({ data: [] })),
      ]);
      setDossiers(resp.data);
      setActivites(actResp.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du chargement des dossiers');
    } finally {
      setLoading(false);
    }
  }, [viewAsFormateur]);

  const fetchModules = useCallback(async () => {
    try {
      const resp = await axios.get(`${API_URL}/modules`);
      setModules(resp.data);
    } catch (err) { /* silencieux */ }
  }, []);

  const fetchAlertesNA = useCallback(async () => {
    try {
      const resp = await axios.get(`${API_URL}/alertes/competences-faibles`);
      setAlertesNA(resp.data || []);
    } catch (err) { setAlertesNA([]); }
  }, []);

  const openActionDetail = useCallback(async (dossierId) => {
    try {
      const params = viewAsFormateur ? `?view_as=${viewAsFormateur.id}` : '';
      const resp = await axios.get(`${API_URL}/portail/formateur/action/${dossierId}${params}`);
      setActionData(resp.data);
      setSelectedAction(dossierId);
    } catch (err) {
      toast.error('Erreur lors du chargement de l\'action');
    }
  }, [viewAsFormateur]);

  const fetchFormateurs = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const resp = await axios.get(`${API_URL}/formateurs`);
      setFormateurs(resp.data);
    } catch (err) { /* silencieux */ }
  }, [isAdmin]);

  useEffect(() => {
    fetchDossiers();
    fetchModules();
    fetchFormateurs();
    fetchAlertesNA();
  }, [fetchDossiers, fetchModules, fetchFormateurs, fetchAlertesNA]);

  // Lien profond depuis Mon Planning : /portail-formateur?dossier=ID&date=YYYY-MM-DD
  const deepDossier = searchParams.get('dossier');
  const deepDate = searchParams.get('date');
  useEffect(() => {
    if (deepDossier && deepDate) {
      openJournee(deepDossier, deepDate);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepDossier, deepDate]);

  const openJournee = async (dossierId, date) => {
    try {
      const params = viewAsFormateur ? `?view_as=${viewAsFormateur.id}` : '';
      const resp = await axios.get(`${API_URL}/portail/formateur/journee/${dossierId}/${date}${params}`);
      setJourneeData(resp.data);
      setSelectedJournee({ dossier_id: dossierId, date });
      setHasEvaluation(!!resp.data.has_evaluations);

      const existing = resp.data.cahier_de_texte;
      const moduleInfo = resp.data.module_info;
      const candidats = resp.data.candidats || [];

      // Initialize evaluations from module definition
      let evals = [];
      if (existing?.evaluations_realisees?.length > 0) {
        evals = existing.evaluations_realisees;
      } else if (moduleInfo?.evaluations) {
        evals = moduleInfo.evaluations.map(ev => ({
          evaluation_type: ev.type,
          evaluation_sous_type: ev.sous_type || '',
          statut: 'a_faire',
          lien_google_form: ev.details || '',
          commentaire: '',
        }));
      }

      // Initialize modules_realises from existing cahier or from planned modules
      let modulesRealises = [];
      if (existing?.modules_realises?.length > 0) {
        modulesRealises = existing.modules_realises;
      } else {
        // Pre-fill from planned modules_pedagogiques
        const plannedModules = resp.data.planning_day?.modules_pedagogiques || [];
        if (plannedModules.length > 0) {
          modulesRealises = plannedModules
            .filter(mp => mp.module_id)
            .map(mp => {
              const mod = modules.find(m => m.id === mp.module_id);
              return {
                module_id: mp.module_id,
                titre: mod?.titre || mp.module_id,
                duree_heures: mp.duree_heures || 7,
              };
            });
        } else if (moduleInfo) {
          modulesRealises = [{
            module_id: moduleInfo.id,
            titre: moduleInfo.titre,
            duree_heures: 7,
          }];
        }
      }

      // Merge presences: ensure ALL candidats from dossier are present, with saved status
      const existingPresMap = {};
      (existing?.presences || []).forEach(p => { existingPresMap[p.candidat_id] = p; });
      const enAftInit = resp.data.candidats_en_aft || {};
      const mergedPresences = candidats.map(c => existingPresMap[c.id] || (enAftInit[c.id] ? { candidat_id: c.id, statut: 'en_aft', aft_code: enAftInit[c.id].aft_code, heure_arrivee: '', commentaire: '' } : { candidat_id: c.id, statut: 'present', heure_arrivee: '', commentaire: '' }));

      // Merge observations: ensure ALL candidats from dossier are present
      const existingObsMap = {};
      (existing?.observations_candidats || []).forEach(o => { existingObsMap[o.candidat_id] = o; });
      const mergedObservations = candidats.map(c => existingObsMap[c.id] || { candidat_id: c.id, texte: '' });

      setCahier({
        module_realise_id: existing?.module_realise_id || moduleInfo?.id || '',
        module_realise_titre: existing?.module_realise_titre || moduleInfo?.titre || '',
        modules_realises: modulesRealises,
        modules_confirmes: existing?.modules_confirmes || false,
        presences: mergedPresences,
        presences_validees: existing?.presences_validees || false,
        abandons: existing?.abandons || [],
        deroule: existing?.deroule || '',
        exercices: existing?.exercices || '',
        cas_pratique: existing?.cas_pratique || '',
        observations_groupe: existing?.observations_groupe || '',
        actions_preconisations: existing?.actions_preconisations || '',
        note_interne: existing?.note_interne || '',
        evaluations_realisees: evals,
        observations_candidats: mergedObservations,
        modules_candidats: existing?.modules_candidats || [],
        dispatch_modules: existing?.dispatch_modules || [],
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const saveJournee = async (override) => {
    if (!selectedJournee) return;
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/portail/formateur/journee/${selectedJournee.dossier_id}/${selectedJournee.date}`,
        override || cahier
      );
      toast.success('Cahier de texte sauvegardé');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Week view helpers
  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentWeek, { weekStartsOn: 1 });
  // le formateur ne peut remonter que d'un mois dans son planning
  const limitePasse = startOfWeek(subMonths(new Date(), 1), { weekStartsOn: 1 });
  const peutReculer = isAdmin || !isBefore(subWeeks(weekStart, 1), limitePasse);
  const actionsEnCours = dossiers.filter(d => !d.echue);
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd }).filter(d => d.getDay() !== 0); // Exclude Sunday

  // Map dossier days to the current week
  const getDayEvents = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return dossiers.filter(d => d.assigned_dates.includes(dateStr)).map(d => ({ ...d, date: dateStr }));
  };

  // All assigned dates for the calendar
  const allDates = dossiers.flatMap(d => d.assigned_dates.map(date => ({ dossier_id: d.id, date, intitule: d.intitule, is_assigned: d.is_assigned })));

  // If viewing a journee, render the cahier de texte
  if (selectedJournee && journeeData) {
    return (
      <DashboardLayout>
        <JourneeView
          journeeData={journeeData}
          cahier={cahier}
          setCahier={setCahier}
          modules={modules}
          saving={saving}
          onSave={saveJournee}
          onBack={() => {
            setSelectedJournee(null); setJourneeData(null); setHasEvaluation(false);
            fetchDossiers();
            if (selectedAction) openActionDetail(selectedAction);
          }}
          hasEvaluation={hasEvaluation}
          setHasEvaluation={setHasEvaluation}
          onRefresh={() => openJournee(selectedJournee.dossier_id, selectedJournee.date)}
          onOpenAction={(dossierId, tab = 'detail') => { setSelectedJournee(null); setJourneeData(null); setHasEvaluation(false); setRapportsVue(tab === 'bilan' ? 'bilan' : 'rapport'); setActionTab(tab === 'bilan' ? 'rapports' : tab); openActionDetail(dossierId); }}
        />
      </DashboardLayout>
    );
  }

  // If viewing an action detail
  if (selectedAction && actionData) {
    return (
      <DashboardLayout>
        <ActionDetailView
          data={actionData}
          viewAsFormateur={viewAsFormateur}
          formateurs={formateurs}
          onBack={() => { setSelectedAction(null); setActionData(null); }}
          onOpenJournee={(dossierId, date) => openJournee(dossierId, date)}
          onRefresh={() => openActionDetail(selectedAction)}
          activeTab={actionTab}
          setActiveTab={setActionTab}
          rapportsVue={rapportsVue}
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
    <div className="max-w-7xl mx-auto space-y-6 p-6" data-testid="portail-formateur">
      {!isAdmin && <InstallBanner appName="OF Manager Formateur" />}
      {/* Admin: formateur selector */}
      {isAdmin && (
        <Card className="border-indigo-200 bg-indigo-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm font-medium text-indigo-700">
                <Eye className="w-4 h-4" />
                Vue admin — Sélectionnez un formateur :
              </div>
              <Select
                value={viewAsFormateur?.id || 'none'}
                onValueChange={(v) => {
                  if (v === 'none') {
                    setViewAsFormateur(null);
                  } else {
                    const f = formateurs.find(f => f.id === v);
                    setViewAsFormateur(f);
                  }
                  setSelectedJournee(null);
                  setJourneeData(null);
                }}
              >
                <SelectTrigger className="w-72 bg-white" data-testid="select-formateur-view">
                  <SelectValue placeholder="Choisir un formateur..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Aucun (vue admin) --</SelectItem>
                  {formateurs.map(f => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.nom} {f.prenom}
                      {f.type_formateur === 'referent' ? ' (Référent)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {viewAsFormateur && (
                <Badge className="bg-indigo-600 text-white">
                  {viewAsFormateur.nom} {viewAsFormateur.prenom}
                  {viewAsFormateur.type_formateur === 'referent' ? ' — Référent' : ' — Standard'}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Portail Formateur
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {viewAsFormateur ? (
              <>Vue de <span className="font-medium text-indigo-600">{viewAsFormateur.nom} {viewAsFormateur.prenom}</span></>
            ) : (
              <>Bienvenue {user?.prenom} {user?.nom}</>
            )}
            {(viewAsFormateur?.type_formateur === 'referent' || (!viewAsFormateur && user?.type_formateur === 'referent')) && (
              <Badge variant="outline" className="ml-2 text-indigo-600 border-indigo-300">Référent</Badge>
            )}
          </p>
        </div>
        {isAdmin && (
          <Button variant="outline" onClick={() => navigate('/')} data-testid="btn-back-dashboard">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Tableau de bord
          </Button>
        )}
      </div>

      <RappelEmargements viewAs={viewAsFormateur?.id} refreshKey={dossiers} onOpen={openJournee} />

      {/* Week navigation */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-2 mb-4">
            <Button variant="outline" size="sm" onClick={() => setCurrentWeek(subWeeks(currentWeek, 1))} disabled={!peutReculer} title={peutReculer ? '' : 'Historique limité à 1 mois'} data-testid="week-prev" aria-label="Semaine précédente">
              <ChevronLeft className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Semaine précédente</span>
            </Button>
            <h2 className="text-xs sm:text-sm font-semibold text-slate-700 text-center">
              Semaine du {format(weekStart, 'd MMMM', { locale: fr })} au {format(weekEnd, 'd MMMM yyyy', { locale: fr })}
            </h2>
            <Button variant="outline" size="sm" onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))} data-testid="week-next" aria-label="Semaine suivante">
              <span className="hidden sm:inline">Semaine suivante</span> <ChevronRight className="w-4 h-4 sm:ml-1" />
            </Button>
          </div>

          {/* Week grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="week-grid">
            {weekDays.map(day => {
              const dateStr = format(day, 'yyyy-MM-dd');
              const events = getDayEvents(day);
              const isToday = isSameDay(day, new Date());
              return (
                <div
                  key={dateStr}
                  className={`border rounded-lg p-2 min-h-[120px] transition-colors ${
                    isToday ? 'border-indigo-400 bg-indigo-50/50' : 'border-slate-200 bg-white'
                  }`}
                >
                  <div className={`text-xs font-medium mb-2 ${isToday ? 'text-indigo-700' : 'text-slate-500'}`}>
                    <span className="capitalize">{format(day, 'EEEE', { locale: fr })}</span>
                    <span className="ml-1 font-bold">{format(day, 'd')}</span>
                  </div>
                  <div className="space-y-1">
                    {[...events].sort((a, b) => (a.type_dossier === 'aft') - (b.type_dossier === 'aft')).map(ev => (
                      <button
                        key={`${ev.id}-${dateStr}`}
                        type="button"
                        onClick={() => openJournee(ev.id, dateStr)}
                        className={`w-full text-left px-2 py-1.5 rounded text-[11px] transition-colors ${
                          ev.is_assigned && !(ev.cahier_valide_dates || []).includes(dateStr) && !(ev.stage_dates || []).includes(dateStr) && dateStr <= format(new Date(), 'yyyy-MM-dd')
                            ? 'bg-amber-50 text-amber-900 border border-amber-400 hover:bg-amber-100 cursor-pointer'
                            : ev.type_dossier === 'aft'
                            ? 'bg-cyan-100 text-cyan-900 border border-cyan-300 hover:bg-cyan-200 cursor-pointer'
                            : ev.is_assigned
                            ? 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200 cursor-pointer'
                            : 'bg-slate-100 text-slate-500 hover:bg-slate-200 cursor-pointer'
                        }`}
                        data-testid={`event-${ev.id}-${dateStr}`}
                      >
                        {(() => {
                          const saisi = (ev.cahier_dates || []).includes(dateStr);
                          const valide = (ev.cahier_valide_dates || []).includes(dateStr);
                          // Journée passée (ou aujourd'hui) affectée au formateur sans présences validées → rappel orange
                          const aValider = ev.is_assigned && !valide && !(ev.stage_dates || []).includes(dateStr) && dateStr <= format(new Date(), 'yyyy-MM-dd');
                          const Ico = ev.is_assigned ? Pencil : Eye;
                          const SaisiBadge = aValider
                            ? <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-amber-800 bg-amber-200/80 px-1 py-px rounded shrink-0" title="Présences non validées : ouvrez la journée pour valider la fiche d'émargement" data-testid={`presences-a-valider-${ev.id}-${dateStr}`}><AlertTriangle className="w-2.5 h-2.5" /> À valider</span>
                            : valide
                            ? <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-emerald-700 shrink-0" title="Présences validées" data-testid={`cahier-saisi-${ev.id}-${dateStr}`}><CheckCircle2 className="w-2.5 h-2.5" /> Validé</span>
                            : saisi ? <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-emerald-700 shrink-0" title="Cahier de texte saisi" data-testid={`cahier-saisi-${ev.id}-${dateStr}`}><CheckCircle2 className="w-2.5 h-2.5" /> Saisi</span> : null;
                          if (ev.type_dossier === 'aft') {
                            return (
                              <>
                                <div className="flex items-center justify-between gap-1">
                                  <span className="flex items-center gap-1 text-[9px] font-bold text-cyan-700 font-mono truncate"><CornerDownRight className="w-3 h-3 shrink-0" /> AFT · {ev.code}</span>
                                  <Ico className="w-2.5 h-2.5 shrink-0" title={ev.is_assigned ? 'Modifier' : 'Consulter'} />
                                </div>
                                <div className="font-medium break-words leading-snug text-[10px]" title={ev.intitule} data-testid={`event-aft-titre-${ev.id}-${dateStr}`}>{(ev.intitule || '').replace(/^Session d'examen\s*/i, '')}</div>
                                <div className="text-[9px] text-violet-700 font-medium" data-testid={`event-aft-nb-${ev.id}-${dateStr}`}>{ev.candidats_count} candidat(s) à examiner</div>
                                {ev.af_mere_code && (
                                  <div className="flex items-start justify-between gap-1 mt-0.5 text-[9px] text-cyan-800" data-testid={`event-aft-mere-${ev.id}-${dateStr}`}>
                                    <span className="break-words min-w-0">→ <span className="font-mono font-semibold">{ev.af_mere_code}</span>{ev.af_mere_intitule ? ` ${ev.af_mere_intitule}` : ''}{ev.meme_formateur_mere ? ' (votre AF)' : ''}</span>
                                    {SaisiBadge}
                                  </div>
                                )}
                              </>
                            );
                          }
                          return (
                            <>
                              <div className="flex items-center justify-between gap-1">
                                <span className="text-[9px] font-mono font-bold truncate" data-testid={`event-code-${ev.id}-${dateStr}`}>{ev.code}</span>
                                <Ico className="w-2.5 h-2.5 shrink-0" title={ev.is_assigned ? 'Modifier' : 'Consulter'} />
                              </div>
                              <div className="flex items-center justify-between gap-1 mt-0.5">
                                <span className="font-medium truncate" title={ev.intitule}>{ev.intitule}</span>
                                {SaisiBadge}
                              </div>
                            </>
                          );
                        })()}
                      </button>
                    ))}
                    {activites.filter(a => a.date === dateStr).map(a => {
                      const c = activiteColor(a.type_affectation);
                      return (
                        <button key={a.id} type="button" onClick={() => setActiviteOpen(a)}
                          className={`w-full text-left px-2 py-1.5 rounded text-[11px] transition-colors ${c.chip}`} data-testid={`activite-${a.id}`}>
                          <div className="font-medium truncate flex items-center gap-1"><Target className="w-2.5 h-2.5 shrink-0" /> {a.label}</div>
                          {a.objectif && <div className="text-[10px] opacity-80 truncate mt-0.5">{a.objectif}</div>}
                          <div className="flex items-center gap-1 mt-0.5">
                            <Pencil className="w-2.5 h-2.5" /><span>Saisir le réalisé</span>
                            {a.realise && <span className="ml-auto inline-flex items-center gap-0.5 text-[9px] font-semibold text-emerald-700"><CheckCircle2 className="w-2.5 h-2.5" /> Réalisé</span>}
                          </div>
                        </button>
                      );
                    })}
                    {events.length === 0 && !activites.some(a => a.date === dateStr) && (
                      <p className="text-[10px] text-slate-300 text-center mt-4">Aucune session</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
      <ActiviteFormateurDialog activite={activiteOpen} open={!!activiteOpen} onOpenChange={(o) => !o && setActiviteOpen(null)}
        canEdit={!viewAsFormateur || isAdmin} onSaved={(a) => setActivites(prev => prev.map(x => x.id === a.id ? { ...x, ...a } : x))} />

      {/* Alerte compétences Non Acquis */}
      {alertesNA.length > 0 && (
        <Card className="border border-red-200 bg-red-50/40" data-testid="formateur-alertes-na-card">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-red-800 flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4" />
              Candidats en difficulté — compétences "Non Acquis" ({alertesNA.length})
            </h3>
            <div className="space-y-1.5">
              {alertesNA.map((a, i) => (
                <div key={i} className="flex items-center justify-between gap-3 bg-white rounded-lg px-3 py-2 border border-red-100" data-testid={`formateur-alerte-na-${a.candidat_id}`}>
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <CandidatLink dossierId={a.dossier_id} candidatId={a.candidat_id} className="text-sm font-medium">{a.candidat_nom}</CandidatLink>
                    {a.dossier_code && (
                      <button
                        type="button"
                        onClick={() => { setActionTab('detail'); openActionDetail(a.dossier_id); }}
                        className="text-[10px] font-mono text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded hover:bg-indigo-100"
                      >
                        {a.dossier_code}
                      </button>
                    )}
                    <span className="text-[10px] text-slate-400 truncate">
                      {(a.details || []).slice(0, 3).map(d => d.competence || d.test_titre).filter(Boolean).join(' · ')}
                    </span>
                  </div>
                  <span className="text-[10px] font-bold text-red-700 bg-red-100 px-2 py-0.5 rounded-full shrink-0">{a.nb_na} NA</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions de certification (AFT) */}
      {actionsEnCours.some(d => d.type_dossier === 'aft') && (
        <Card className="border-cyan-200">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-cyan-800 mb-3 flex items-center gap-2">
              <GraduationCap className="w-4 h-4" /> Mes actions de certification (AFT)
            </h3>
            <div className="divide-y" data-testid="mes-afts-list">
              {actionsEnCours.filter(d => d.type_dossier === 'aft').map(d => (
                <div
                  key={d.id}
                  className="py-3 flex items-center justify-between cursor-pointer hover:bg-cyan-50/50 px-2 -mx-2 rounded-md transition-colors"
                  data-testid={`aft-row-${d.id}`}
                  onClick={() => { setActionTab('detail'); openActionDetail(d.id); }}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-mono text-cyan-700 bg-cyan-50 px-1.5 py-0.5 rounded">{d.code}</span>
                    <span className="text-[9px] font-bold text-cyan-700 bg-cyan-100 px-1.5 py-0.5 rounded">AFT</span>
                    {d.module_certifiant && (
                      <span className="text-xs font-medium text-rose-700 bg-rose-50 px-1.5 py-0.5 rounded">{d.module_certifiant.code} — {d.module_certifiant.titre}</span>
                    )}
                    {d.af_mere_code && <span className="text-xs text-slate-500 inline-flex items-center gap-1"><CornerDownRight className="w-3 h-3" /> {d.meme_formateur_mere ? 'rattachée à votre AF' : 'AF mère'} <span className="font-mono font-semibold">{d.af_mere_code}</span>{d.af_mere_intitule ? ` — ${d.af_mere_intitule}` : ''}</span>}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    <span>{d.candidats_count || 0} candidat(s)</span>
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{d.assigned_dates.length} jour(s)</span>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dossiers list */}
      <Card>
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Mes actions de formation <span className="text-xs font-normal text-slate-400">— en cours uniquement (les actions terminées ou dont la date de fin est dépassée sont masquées)</span></h3>
          <div className="divide-y">
            {actionsEnCours.filter(d => d.type_dossier !== 'aft').length === 0 && <p className="py-3 text-xs text-slate-400 italic" data-testid="aucune-action-en-cours">Aucune action de formation en cours.</p>}
            {actionsEnCours.filter(d => d.type_dossier !== 'aft').map(d => (
              <div
                key={d.id}
                className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 cursor-pointer hover:bg-slate-50 px-2 -mx-2 rounded-md transition-colors"
                data-testid={`dossier-row-${d.id}`}
                onClick={() => { setActionTab('detail'); openActionDetail(d.id); }}
              >
                <div className="min-w-0">
                  <span className="text-xs font-mono text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded mr-2 whitespace-nowrap">{d.code}</span>
                  <span className="text-sm font-medium text-slate-800">{d.intitule}</span>
                  {d.client && <span className="text-xs text-slate-400 ml-2">{d.client}</span>}
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500 shrink-0">
                  <span className="whitespace-nowrap">{d.candidats_count || 0} candidat(s)</span>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <Calendar className="w-3 h-3" />
                    {d.assigned_dates.length} jour(s)
                  </span>
                  <div className="flex items-center gap-2 flex-1 sm:flex-none sm:min-w-[120px]">
                    <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${d.progression >= 100 ? 'bg-emerald-500' : d.progression > 50 ? 'bg-indigo-500' : d.progression > 0 ? 'bg-amber-500' : 'bg-slate-300'}`}
                        style={{ width: `${d.progression || 0}%` }}
                      />
                    </div>
                    <span className={`font-semibold tabular-nums ${d.progression >= 100 ? 'text-emerald-600' : 'text-slate-500'}`}>{d.progression || 0}%</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
    </DashboardLayout>
  );

// ============== Journée Detail View (Cahier de Texte) ==============
};

export default PortailFormateurPage;


// ============== Evaluations Manuelles Panel ==============
const EvaluationsManuellesPanel = ({ dossierId, formation: formationProp }) => {
  const [evaluations, setEvaluations] = useState([]);
  const [modules, setModules] = useState([]);
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(true);
  const headers = useMemo(() => ({ Authorization: `Bearer ${getAuthToken()}` }), []);

  useEffect(() => {
    const fetch = async () => {
      try {
        const modsRes = await axios.get(`${API_URL}/modules`, { headers });
        setModules(modsRes.data);

        // Get formation module IDs from prop
        const formMods = formationProp?.modules_formation || formationProp?.modules || [];
        const formModIds = formMods.map(m => m.module_id).filter(Boolean);

        if (formModIds.length === 0) {
          setLoading(false);
          return;
        }

        // Fetch evaluations for each module
        let allEvals = [];
        for (const mid of formModIds) {
          try {
            const res = await axios.get(`${API_URL}/evaluations-manuelles/by-module/${mid}`, { headers });
            allEvals = [...allEvals, ...res.data];
          } catch (err) { /* silencieux */ }
        }
        setEvaluations(allEvals);

        // Fetch results for each evaluation
        const resMap = {};
        for (const ev of allEvals) {
          try {
            const rr = await axios.get(`${API_URL}/evaluations-manuelles/${ev.id}/resultats-dossier/${dossierId}`, { headers });
            resMap[ev.id] = rr.data;
          } catch (err) { /* silencieux */ }
        }
        setResults(resMap);
      } catch (err) { /* silencieux */ }
      setLoading(false);
    };
    fetch();
  }, [dossierId, formationProp, headers]);

  const getModuleName = (mid) => {
    const m = modules.find(mod => mod.id === mid);
    return m ? `${m.code} ${m.titre}` : '';
  };

  const TYPE_LABELS = { exercice: 'EXO', cas_pratique: 'CAS', application: 'APP', examen: 'EXA', atelier: 'ATE', certification: 'CERT' };
  const TYPE_COLORS = { exercice: 'bg-blue-100 text-blue-700', cas_pratique: 'bg-amber-100 text-amber-700', application: 'bg-purple-100 text-purple-700', examen: 'bg-red-100 text-red-700', atelier: 'bg-emerald-100 text-emerald-700', certification: 'bg-rose-100 text-rose-700' };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>;
  if (evaluations.length === 0) return null;

  return (
    <Card className="border-0 shadow-sm" data-testid="evaluations-manuelles-panel">
      <CardContent className="p-5">
        <h2 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4 text-amber-500" /> Évaluations manuelles ({evaluations.length})
        </h2>
        <div className="space-y-3">
          {evaluations.map(ev => {
            const res = results[ev.id];
            const passages = res?.passages || [];
            const nbEvalue = passages.length;
            const total = nbEvalue + (res?.non_evalues || []).length;
            const typeLabel = TYPE_LABELS[ev.type_evaluation] || ev.type_evaluation;
            const typeColor = TYPE_COLORS[ev.type_evaluation] || 'bg-slate-100 text-slate-700';

            return (
              <div key={ev.id} className="p-3 bg-slate-50 rounded-lg" data-testid={`eval-panel-${ev.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={`${typeColor} text-[10px] px-1.5 py-0 font-semibold`}>{typeLabel}</Badge>
                    <span className="text-sm font-medium text-slate-800">{ev.code} {ev.titre}</span>
                    <span className="text-[10px] text-slate-400">{getModuleName(ev.module_id)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {nbEvalue > 0 ? (
                      <Badge className="bg-emerald-100 text-emerald-700 text-[10px] px-1.5 py-0 font-medium">
                        <CheckCircle2 className="w-3 h-3 mr-0.5" /> {nbEvalue}/{total} évalué(s)
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-slate-400">Non réalisé</Badge>
                    )}
                  </div>
                </div>
                {nbEvalue > 0 && (
                  <div className="mt-2 space-y-1">
                    {passages.map(p => {
                      const color = p.statut_global === 'A' ? 'text-emerald-600' : p.statut_global === 'ECA' ? 'text-amber-600' : 'text-red-600';
                      return (
                        <div key={p.id} className="flex items-center justify-between text-xs py-1 px-2 bg-white rounded">
                          <CandidatLink dossierId={dossierId} candidatId={p.candidat_id} className="font-medium">{p.candidat_nom}</CandidatLink>
                          <div className="flex items-center gap-2">
                            <span className={`font-bold ${color}`}>{p.moyenne}%</span>
                            <Badge className={`text-[10px] px-1 py-0 ${color} bg-white border`}>{p.statut_global}</Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// ============== Fiche Formation Panel ==============
const FicheFormationPanel = ({ formation, certification }) => {
  const [modules, setModules] = useState([]);
  const [moduleFiltre, setModuleFiltre] = useState('all');
  const headers = useMemo(() => ({ Authorization: `Bearer ${getAuthToken()}` }), []);

  useEffect(() => {
    const fetchModules = async () => {
      if (!formation) return;
      const modIds = (formation.modules_formation || formation.modules || []).map(m => m.module_id).filter(Boolean);
      if (modIds.length === 0) return;
      try {
        const res = await axios.get(`${API_URL}/modules`, { headers });
        setModules(res.data.filter(m => modIds.includes(m.id)));
      } catch (err) { /* silencieux */ }
    };
    fetchModules();
  }, [formation, headers]);

  if (!formation) {
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="p-12 text-center">
          <p className="text-slate-400">Aucune formation associée à ce dossier.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="fiche-formation-panel">
      {/* Info principale */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-5">
          <h2 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <GraduationCap className="w-4 h-4 text-indigo-500" /> {formation.titre}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div><span className="text-slate-400 text-xs">Code</span><p className="font-medium text-slate-800">{formation.code}</p></div>
            <div><span className="text-slate-400 text-xs">Type</span><p className="font-medium text-slate-800">{formation.type_formation || '—'}</p></div>
            <div><span className="text-slate-400 text-xs">Durée totale</span><p className="font-medium text-slate-800">{formation.duree_totale_heures}h</p></div>
            {formation.niveau_acces && <div><span className="text-slate-400 text-xs">Niveau d'accès</span><p className="font-medium text-slate-800">{formation.niveau_acces}</p></div>}
            {formation.niveau_sortie && <div><span className="text-slate-400 text-xs">Niveau de sortie</span><p className="font-medium text-slate-800">{formation.niveau_sortie}</p></div>}
            {formation.reference_rncp && <div><span className="text-slate-400 text-xs">Réf. RNCP</span><p className="font-medium text-slate-800">{formation.reference_rncp}</p></div>}
          </div>
        </CardContent>
      </Card>

      {/* Certification */}
      {certification && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5">
            <h2 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Award className="w-4 h-4 text-amber-500" /> Certification
            </h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-slate-400 text-xs">Code RNCP</span><p className="font-medium text-slate-800">{certification.code_rncp}</p></div>
              <div><span className="text-slate-400 text-xs">Titre</span><p className="font-medium text-slate-800">{certification.titre}</p></div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Objectif pédagogique */}
      {formation.objectif_pedagogique && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5">
            <h2 className="text-sm font-semibold text-slate-700 mb-2">Objectif pédagogique</h2>
            <p className="text-sm text-slate-600 whitespace-pre-line">{formation.objectif_pedagogique}</p>
          </CardContent>
        </Card>
      )}

      {/* Compétences professionnelles */}
      {(formation.competences_pro?.length > 0 || formation.competences_professionnelles) && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5">
            <h2 className="text-sm font-semibold text-slate-700 mb-3">Compétences professionnelles à valider</h2>
            <CompetencesProList competences={formation.competences_pro} legacyText={formation.competences_professionnelles} />
          </CardContent>
        </Card>
      )}

      {/* Modules */}
      {modules.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-indigo-500" /> Modules ({modules.length}) — QCM & évaluations à faire passer
              </h2>
              <Select value={moduleFiltre} onValueChange={setModuleFiltre}>
                <SelectTrigger className="h-8 text-xs w-[240px]" data-testid="fiche-formation-filtre-module"><SelectValue placeholder="Filtrer par module" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Tous les modules</SelectItem>{modules.map(m => <SelectItem key={m.id} value={m.id}>{m.code} — {m.titre}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              {modules.filter(m => moduleFiltre === 'all' || m.id === moduleFiltre).map(m => (
                <div key={m.id} className="flex flex-wrap items-start gap-3 p-3 bg-slate-50 rounded-lg" data-testid={`fiche-module-${m.id}`}>
                  <span className="text-xs font-mono text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded font-semibold">{m.code}</span>
                  <div className="flex-1 min-w-0 space-y-1">
                    <p className="text-sm font-medium text-slate-800 truncate">{m.titre}</p>
                    {m.duree_heures && <p className="text-[10px] text-slate-400">{m.duree_heures}h</p>}
                    <TestsLiesModule moduleId={m.id} />
                  </div>
                  {m.sequence && <Badge variant="outline" className="text-[10px]">{m.sequence}</Badge>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modalités */}
      {(formation.modalites_evaluation || formation.modalites_organisation) && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5 space-y-4">
            {formation.modalites_evaluation && (
              <div>
                <h2 className="text-sm font-semibold text-slate-700 mb-2">Modalités d'évaluation</h2>
                <p className="text-sm text-slate-600 whitespace-pre-line">{formation.modalites_evaluation}</p>
              </div>
            )}
            {formation.modalites_organisation && (
              <div>
                <h2 className="text-sm font-semibold text-slate-700 mb-2">Modalités d'organisation</h2>
                <p className="text-sm text-slate-600 whitespace-pre-line">{formation.modalites_organisation}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ============== Action Detail View ==============
const ActionDetailView = ({ data, viewAsFormateur, formateurs, onBack, onOpenJournee, onRefresh, activeTab, setActiveTab, rapportsVue = 'rapport' }) => {
  const { dossier, formation, certification, timeline, candidats } = data;
  const [selectedCandidat, setSelectedCandidat] = useState(null);
  const [ficheVisuelle, setFicheVisuelle] = useState(null);
  const [assiduite, setAssiduite] = useState(null);
  const candidatsActifs = candidats.filter(c => !c.abandon);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/portail/formateur/action/${dossier.id}/assiduite`)
      .then(r => { if (!cancelled) setAssiduite(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [dossier.id, timeline]);

  // Candidat detail sub-view
  if (selectedCandidat) {
    return (
      <FicheCandidatAction
        dossier={dossier}
        candidat={selectedCandidat}
        assiduite={assiduite}
        onBack={() => setSelectedCandidat(null)}
        onCandidatUpdated={(updated) => { setSelectedCandidat({ ...selectedCandidat, ...updated }); onRefresh?.(); }}
        onAbandonChanged={(cid, a) => { setSelectedCandidat({ ...selectedCandidat, abandon: a }); onRefresh?.(); }}
      />
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour
          </Button>
          <div>
            <span className="text-xs font-mono text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">{dossier.code}</span>
            <h1 className="text-xl font-bold text-slate-900 mt-1">{dossier.intitule}</h1>
          </div>
        </div>
        <Badge className="bg-blue-100 text-blue-700">{dossier.statut === 'en_cours' ? 'En cours' : dossier.statut}</Badge>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 pb-1">
        <TabsList className="bg-slate-100 w-max">
          <TabsTrigger value="detail">Détail & Organisation</TabsTrigger>
          <TabsTrigger value="timeline">Fil chronologique ({timeline.length})</TabsTrigger>
          <TabsTrigger value="candidats">Candidats ({candidatsActifs.length})</TabsTrigger>
          <TabsTrigger value="qcm" data-testid="tab-qcm">Évaluations</TabsTrigger>
          <TabsTrigger value="suivi" data-testid="tab-suivi-pedagogique">Suivi pédagogique</TabsTrigger>
          <TabsTrigger value="fiche-formation" data-testid="tab-fiche-formation">Fiche Formation</TabsTrigger>
          <TabsTrigger value="rapports" data-testid="tab-rapports-action">Rapports</TabsTrigger>
        </TabsList>
        </div>

        <TabsContent value="rapports">
          <RapportDossierPanel key={rapportsVue} dossierId={dossier.id} initialVue={rapportsVue} />
        </TabsContent>

        {/* DETAIL TAB */}
        <TabsContent value="detail" className="space-y-4">
          {/* Action info */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-5">
              <h2 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><FileText className="w-4 h-4 text-indigo-500" /> Informations de l'action</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div><span className="text-slate-400 text-xs">Client</span><p className="font-medium text-slate-800">{dossier.client || '—'}</p></div>
                <div><span className="text-slate-400 text-xs">Début</span><p className="font-medium text-slate-800">{dossier.date_debut || '—'}</p></div>
                <div><span className="text-slate-400 text-xs">Fin</span><p className="font-medium text-slate-800">{dossier.date_fin || '—'}</p></div>
                <div><span className="text-slate-400 text-xs">Lieu principal</span><p className="font-medium text-slate-800">{dossier.lieu_principal || '—'}</p></div>
              </div>
              {dossier.periodes_stage?.length > 0 && (
                <div className="mt-3 flex gap-2">
                  {dossier.periodes_stage.map((pae) => (
                    <span key={`${pae.date_debut}-${pae.date_fin}`} className="text-[10px] font-bold text-violet-700 bg-violet-100 px-2 py-0.5 rounded">{pae.label} : {pae.date_debut} → {pae.date_fin}</span>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Formation info */}
          {formation && (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-5">
                <h2 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><GraduationCap className="w-4 h-4 text-indigo-500" /> Formation : {formation.titre}</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div><span className="text-slate-400 text-xs">Code</span><p className="font-medium text-slate-800">{formation.code}</p></div>
                  <div><span className="text-slate-400 text-xs">Type</span><p className="font-medium text-slate-800">{formation.type_formation || '—'}</p></div>
                  <div><span className="text-slate-400 text-xs">Durée</span><p className="font-medium text-slate-800">{formation.duree_totale_heures}h</p></div>
                  <div><span className="text-slate-400 text-xs">Modules</span><p className="font-medium text-slate-800">{(formation.modules_formation || formation.modules || []).length}</p></div>
                </div>
                {certification && (
                  <div className="mt-3 p-2 bg-indigo-50 rounded-md text-xs">
                    <span className="font-semibold text-indigo-700">{certification.code_rncp}</span>
                    <span className="text-slate-600 ml-2">{certification.titre}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Workflow Pédagogique */}
          <WorkflowPanel
            actionFormationId={dossier.id}
            typeWorkflow="pedagogique"
            formateurs={formateurs || []}
          />

          {/* Notes sur la formation */}
          <NotesPanel dossierId={dossier.id} title="Notes & observations sur la formation" testId="notes-formation" />
        </TabsContent>

        {/* TIMELINE TAB */}
        <TabsContent value="timeline">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-5">
              <h2 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2"><Clock className="w-4 h-4 text-indigo-500" /> Fil chronologique</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-100 text-slate-500 uppercase tracking-wide">
                      <th className="px-3 py-2 text-left font-medium">Date</th>
                      <th className="px-3 py-2 text-left font-medium">Type</th>
                      <th className="px-3 py-2 text-left font-medium">Formateur</th>
                      <th className="px-3 py-2 text-left font-medium">Lieu</th>
                      <th className="px-3 py-2 text-left font-medium">Module(s)</th>
                      <th className="px-3 py-2 text-center font-medium">Présences</th>
                      <th className="px-3 py-2 text-center font-medium">Évaluations</th>
                      <th className="px-3 py-2 text-center font-medium">Cahier</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {timeline.map(t => {
                      const d = new Date(t.date + 'T00:00:00');
                      const dayLabel = d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
                      const isStage = t.type_journee === 'stage';
                      const isPast = d <= new Date();
                      return (
                        <tr
                          key={t.date}
                          className={`transition-colors ${isStage ? 'bg-violet-50/50' : isPast && !t.has_cahier ? 'bg-amber-50/30' : 'hover:bg-slate-50'} cursor-pointer`}
                          onClick={() => !isStage && onOpenJournee(dossier.id, t.date)}
                          data-testid={`timeline-row-${t.date}`}
                        >
                          <td className="px-3 py-2 font-medium text-slate-700 whitespace-nowrap">{dayLabel}</td>
                          <td className="px-3 py-2">
                            {isStage ? (
                              <span className="text-[10px] font-bold text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded">{t.organisme_externe || 'Stage'}</span>
                            ) : (
                              <span className="text-slate-600 capitalize">{t.type_journee}</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-slate-600">{t.formateur_nom || <span className="text-slate-300">—</span>}</td>
                          <td className="px-3 py-2 text-slate-600">{isStage ? '—' : (t.lieu || <span className="text-slate-300">—</span>)}</td>
                          <td className="px-3 py-2 text-slate-600 max-w-[300px]">
                            {t.modules.length > 0 ? (
                              <div className="space-y-0.5">
                                {t.modules.map((m) => (
                                  <div key={m} className="text-xs truncate" title={m}>{m}</div>
                                ))}
                              </div>
                            ) : <span className="text-slate-300">—</span>}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {isStage ? '—' : t.presences_validees ? (
                              <span className="text-emerald-600 font-semibold flex items-center justify-center gap-1"><Check className="w-3 h-3" /> Oui</span>
                            ) : isPast ? (
                              <span className="text-amber-500 font-medium">Non</span>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {t.eval_detail ? (
                              <div className="flex flex-col items-center gap-0.5">
                                <span className={`text-[11px] font-bold ${t.eval_detail.candidats_evalues >= t.eval_detail.total_candidats ? 'text-emerald-600' : 'text-indigo-600'}`}>
                                  {t.eval_detail.candidats_evalues}/{t.eval_detail.total_candidats} passé
                                </span>
                                {t.eval_detail.moyenne !== null && t.eval_detail.moyenne !== undefined && (
                                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                                    t.eval_detail.moyenne >= 70 ? 'bg-emerald-50 text-emerald-700' :
                                    t.eval_detail.moyenne >= 50 ? 'bg-amber-50 text-amber-700' :
                                    'bg-red-50 text-red-600'
                                  }`}>
                                    Moy: {t.eval_detail.moyenne}%
                                  </span>
                                )}
                              </div>
                            ) : t.evaluations_count > 0 ? (
                              <span className="text-indigo-600 font-semibold">{t.evaluations_count}</span>
                            ) : <span className="text-slate-300">—</span>}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {t.has_cahier ? (
                              <span className="text-emerald-600"><Check className="w-3.5 h-3.5 mx-auto" /></span>
                            ) : <span className="text-slate-300">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CANDIDATS TAB */}
        <TabsContent value="candidats">
          <CandidatsCadre dossierId={dossier.id} titre="Candidats affectés" onEdit={(c) => { const cand = candidats.find(x => x.id === c.candidat_id); if (cand) setSelectedCandidat(cand); }} />
        </TabsContent>

        {/* EVALUATIONS TAB (QCM + Manuelles) */}
        <TabsContent value="qcm">
          <div className="space-y-6">
            <QCMFormateurPanel dossierId={dossier.id} />
            <EvaluationsManuellesPanel dossierId={dossier.id} formation={formation} />
          </div>
        </TabsContent>

        <TabsContent value="suivi">
          <SuiviPedagogique dossierId={dossier.id} canManage />
        </TabsContent>

        {/* FICHE FORMATION TAB */}
        <TabsContent value="fiche-formation">
          <FicheFormationPanel formation={formation} certification={certification} />
        </TabsContent>
      </Tabs>
      <FicheCandidatEntreprise open={!!ficheVisuelle} candidatId={ficheVisuelle?.id} dossierId={dossier.id} onClose={() => setFicheVisuelle(null)} apiBase={`${API_URL}/portail/formateur`}
        onEdit={ficheVisuelle ? () => { const c = ficheVisuelle; setFicheVisuelle(null); setSelectedCandidat(c); } : undefined} />

    </div>
  );
};
